import { useState } from 'react'
import type { ChatAttachment } from '../../store/types'
import { useAttachmentBlob } from '../../components/useAttachmentBlob'
import { bridge } from '../../bridge'
import './MessageAttachments.css'

// Human-readable byte size for file chips.
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// MessageAttachments renders the images + files attached to a chat message:
// images inline (tap to zoom), everything else as a file chip (tap to open an
// in-app viewer).
export function MessageAttachments({ attachments }: { attachments: ChatAttachment[] }) {
  return (
    <div className="msg-attachments">
      {attachments.map((att) =>
        att.kind === 'image' ? (
          <AttachmentImage key={att.id} att={att} />
        ) : (
          <AttachmentFileChip key={att.id} att={att} />
        ),
      )}
    </div>
  )
}

function AttachmentImage({ att }: { att: ChatAttachment }) {
  const { src, error } = useAttachmentBlob(att.url)
  const [zoomed, setZoomed] = useState(false)

  // Reserve the on-screen box from the known aspect ratio so the bubble doesn't
  // reflow when the image finishes loading. Falls back to a square-ish box when
  // the server couldn't read dimensions.
  const ratio = att.width && att.height ? att.width / att.height : undefined
  const style = ratio ? { aspectRatio: String(ratio) } : undefined

  if (error) {
    return <div className="msg-attachment-img msg-attachment-img--error">Image unavailable</div>
  }
  return (
    <>
      <button
        type="button"
        className="msg-attachment-img"
        style={style}
        onClick={() => src && setZoomed(true)}
        aria-label={`Open ${att.filename}`}
      >
        {src ? (
          <img src={src} alt={att.filename} draggable={false} />
        ) : (
          <span className="msg-attachment-img__spinner" aria-hidden />
        )}
      </button>
      {zoomed && src && (
        <div className="msg-attachment-zoom" onClick={() => setZoomed(false)} role="dialog">
          <img src={src} alt={att.filename} />
        </div>
      )}
    </>
  )
}

// PendingImagePreview shows a small thumbnail of an already-uploaded image in
// the composer strip before it's posted. Reuses the authed blob loader, so the
// same bytes aren't re-fetched once the message renders.
export function PendingImagePreview({ att }: { att: ChatAttachment }) {
  const { src } = useAttachmentBlob(att.url)
  if (!src) return <span className="msg-attachment-img__spinner" aria-hidden />
  return <img src={src} alt={att.filename} draggable={false} />
}

function AttachmentFileChip({ att }: { att: ChatAttachment }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="msg-attachment-chip" onClick={() => setOpen(true)}>
        <span className="msg-attachment-chip__icon" aria-hidden>
          {iconForMime(att.mime_type)}
        </span>
        <span className="msg-attachment-chip__meta">
          <span className="msg-attachment-chip__name">{att.filename}</span>
          <span className="msg-attachment-chip__size">{formatBytes(att.size_bytes)}</span>
        </span>
      </button>
      {open && <AttachmentViewer att={att} onClose={() => setOpen(false)} />}
    </>
  )
}

// AttachmentViewer is the full-screen overlay a file chip opens. It renders the
// already-authed blob inline when we can (PDFs, images), and hands everything
// else to the native OS to open. Rendering/handoff in-app is what makes a tap
// do *something* inside the native shells (WKWebView / Android WebView), where
// `window.open(blob:…)` silently no-ops (no WKUIDelegate / no new-window
// handling) and a bare `UIApplication.open` / Intent can't reach a JS-scoped
// blob URL. WKWebView and Chromium both render PDFs inside an <iframe>, so that
// path shows the document without any native bridge work; the "Open" action
// (native only) pushes the bytes to iOS QuickLook / Android's ACTION_VIEW so
// arbitrary types get a real viewer or an "open with" chooser.
function AttachmentViewer({ att, onClose }: { att: ChatAttachment; onClose: () => void }) {
  const { src, error } = useAttachmentBlob(att.url)
  const inline = inlineKind(att.mime_type)
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState(false)

  async function openNatively() {
    if (!src || opening) return
    setOpening(true)
    setOpenError(false)
    try {
      const data_b64 = await blobURLToBase64(src)
      const { ok } = await bridge.call('files.open', {
        filename: att.filename,
        mime_type: att.mime_type,
        data_b64,
      })
      if (!ok) setOpenError(true)
    } catch {
      setOpenError(true)
    } finally {
      setOpening(false)
    }
  }

  return (
    <div className="msg-attachment-viewer" role="dialog" aria-label={att.filename}>
      <div className="msg-attachment-viewer__bar">
        <span className="msg-attachment-viewer__title" title={att.filename}>
          {att.filename}
        </span>
        {src &&
          (bridge.isNative ? (
            // Hand the bytes to the OS — QuickLook / ACTION_VIEW. Covers every
            // type (incl. the inline ones, so users can share / print / open-in).
            <button
              type="button"
              className="msg-attachment-viewer__action"
              onClick={() => void openNatively()}
              disabled={opening}
            >
              {opening ? 'Opening…' : 'Open'}
            </button>
          ) : (
            // Plain browser: a normal download works and is the natural "save".
            <a
              className="msg-attachment-viewer__action"
              href={src}
              download={att.filename}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </a>
          ))}
        <button
          type="button"
          className="msg-attachment-viewer__action msg-attachment-viewer__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="msg-attachment-viewer__body">
        {error ? (
          <div className="msg-attachment-viewer__msg">Couldn’t load this file.</div>
        ) : !src ? (
          <span className="msg-attachment-img__spinner" aria-hidden />
        ) : inline === 'pdf' ? (
          <iframe className="msg-attachment-viewer__frame" title={att.filename} src={src} />
        ) : inline === 'image' ? (
          <img className="msg-attachment-viewer__img" src={src} alt={att.filename} />
        ) : (
          <div className="msg-attachment-viewer__msg">
            <div className="msg-attachment-viewer__icon" aria-hidden>
              {iconForMime(att.mime_type)}
            </div>
            <div>{att.filename}</div>
            <div className="msg-attachment-viewer__sub">
              {formatBytes(att.size_bytes)}
              {openError
                ? ' · Couldn’t open this file'
                : bridge.isNative
                  ? ' · Tap Open to view'
                  : ' · Preview not available'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Read a same-origin blob: object URL back into raw base64 (no data: prefix)
// for the files.open bridge handoff. The blob is already in memory from the
// authed fetch, so this is a local read, not a network round-trip.
async function blobURLToBase64(url: string): Promise<string> {
  const blob = await (await fetch(url)).blob()
  const dataURL: string = await new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(String(fr.result))
    fr.onerror = () => reject(fr.error)
    fr.readAsDataURL(blob)
  })
  const comma = dataURL.indexOf(',')
  return comma >= 0 ? dataURL.slice(comma + 1) : dataURL
}

// Which types we can render directly in the webview. PDFs render via <iframe>
// (WKWebView / Chromium both have a native viewer); images via <img>.
function inlineKind(mime: string): 'pdf' | 'image' | null {
  if (mime === 'application/pdf') return 'pdf'
  if (mime.startsWith('image/')) return 'image'
  return null
}

function iconForMime(mime: string): string {
  if (mime === 'application/pdf') return '📕'
  if (mime.startsWith('image/')) return '🖼️'
  if (mime.startsWith('audio/')) return '🎵'
  if (mime.startsWith('video/')) return '🎬'
  if (mime.startsWith('text/') || mime === 'application/json') return '📝'
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return '🗜️'
  return '📄'
}
